// components/DepartureReadinessCard.js
//
// Admin dashboard card for the checkout-reminder monitoring surface
// (tasks/checkout-message-automation.md §3.9). Answers ONE question for the day:
// "do today's departing guests know when they have to leave?"
//
// Sits directly BELOW ArrivalReadinessCard, so the page reads arrivals → departures — the guest
// journey in the order ops live it. It is a deliberate TWIN of that card: same shell, same shared
// stylesheet, same badge language, same manual-send/undo/attribution mechanics. A second visual
// language for the same kind of question would make the operator re-learn the page halfway down.
//
// 🕐 WHY IT EXISTS AT ALL: the reminder has a 20-minute send window and no retry. A system that is
// not running is also not recording that it missed anyone, so an empty ledger is indistinguishable
// from a perfect day. This card (and the daily digest behind it) is what makes silence trustworthy —
// which is why it ships BEFORE the send feature is ever enabled.
//
// Three deliberate divergences from the arrival twin:
//   1. ONE day, not two — the reminder fires the same day, so there is no next-day prep window.
//   2. TWO status columns, and they carry DIFFERENT KINDS OF CLAIM — read the note below.
//   3. A COPY button on manual rows. Ops already confirm a manual send but retype the message
//      themselves; it renders from stored data with or without an OTA thread, so the exact text we
//      would have posted is one click away.
//
// 🔴 THE TWO COLUMNS ARE NOT PEERS (D19). «التذكير» is about a MESSAGE the system owns and can
// verify; «تسجيل الخروج» is a human confirming a physical fact nobody watched. The second is the
// weaker evidence, and the card must never let them read as equally certain — which is why the
// checkout column always names its recorder («سجّله فلان») and never claims «الخروج الفعلي» (D16).
// A device source (keypad / NFC) writes into that SAME column later, with its own `source` and the
// three-state model machine evidence earns; it does not get a third column.
//
// 🔴 THE CHECKOUT COLUMN RECORDS, IT DOES NOT GRADE (D2, §9). It notifies nobody, dispatches no
// cleaner, and does NOT feed the row's `overall` verdict — so a day of unrecorded checkouts stays
// green. The five summary chips still partition on `reminder.status` alone and still sum to the row
// count; `checkoutRecorded` sits outside that partition on purpose.
//
// Hardcoded Arabic/RTL + centralized dateFormatter conventions, like every card beside it.

import React, { useState } from 'react'; // useState: copy-button feedback + card collapse
import { useNavigate } from 'react-router-dom';
import {
  useDepartureReadiness,
  useMarkCheckoutMessageSentManually,
  useUndoCheckoutMessageSentManually,
  useRecordGuestCheckout,
  useUndoGuestCheckout
} from '../hooks/useDepartureReadiness';
import useLanguage from '../hooks/useLanguage';
import { formatShortDateTime } from '../utils/dateFormatter';
// The checkout entry is a datetime-local field, which carries NO timezone — these compose it
// against Asia/Riyadh, exactly as the backend does. Never hand-format the value.
import { toRiyadhInputValue, riyadhInputValueToIso } from '../utils/dateValueHelper';
import DEPARTURE_ERROR_CODES, {
  DEPARTURE_REMINDER_CODES,
  DEPARTURE_CHECKOUT_CODES
} from '../utils/departureErrorCodes';
import BookingRefLine from './BookingRefLine';
import RelocationBanner from './RelocationBanner';
import useRelocations from '../hooks/useRelocations';
import { RELOCATION_UNKNOWN_AR } from '../config/relocationLabels';
import './readinessCards.css';

/**
 * True when the row will carry the manual-send attribution line, which states "sent manually" in
 * full. The reminder column's own detail says the same thing, so the two must never render together.
 */
const isManualSendAttributed = (reminder) =>
  reminder?.code === DEPARTURE_REMINDER_CODES.MANUAL_SEND_DONE
  && !!(reminder.sentManuallyBy || reminder.sentManuallyAt);

/**
 * Who confirmed the manual send, and when — on the row itself.
 *
 * The tick is a trust-based assertion with no delivery to verify against, so accountability IS the
 * feature. Without this it would live only in the booking's change log, which nobody opens day to
 * day. Name resolved server-side; the timestamp goes through the centralized formatter.
 */
const ManualSendAttribution = ({ reminder, bookingId }) => {
  const { t, language } = useLanguage();
  if (!isManualSendAttributed(reminder)) return null;

  // ONE line, not two: the attribution carries the whole statement and the column detail is
  // suppressed at the call site, so who and when hang off the sentence that was already there.
  const who = reminder.sentManuallyBy
    ? ` ${t('departureReadiness.manualSend.by')} ${reminder.sentManuallyBy}`
    : '';
  const when = reminder.sentManuallyAt
    ? ` · ${formatShortDateTime(reminder.sentManuallyAt, language)}`
    : '';

  return (
    <small className="text-muted d-block">
      {t('departureReadiness.manualSend.done')}{who}{when}
      <UndoManualSend bookingId={bookingId} />
    </small>
  );
};

/**
 * Un-tick. Kept visually quiet — a plain link, not a button — because this is a correction, not an
 * action anyone should be invited to take. Without it a mis-click leaves the departure reading
 * "handled" with no way back.
 */
const UndoManualSend = ({ bookingId }) => {
  const { t } = useLanguage();
  const { mutate, isPending } = useUndoCheckoutMessageSentManually();

  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0"
      disabled={isPending}
      onClick={(e) => {
        e.stopPropagation();
        mutate(bookingId);
      }}
      style={{ fontSize: '12px', marginInlineStart: '8px', verticalAlign: 'baseline' }}
    >
      {t('departureReadiness.manualSend.undo')}
    </button>
  );
};

// The three possible answers, matching the arrival card exactly. There is deliberately NO amber
// tier: an official amber immediately makes an operator ask "do I own this? do I act?".
const HEADLINE_STYLE = {
  ready: { color: '#0a8f3c', bg: '#eaf7ef', icon: 'bi-check-circle-fill' },
  action: { color: '#cc0000', bg: '#fdeded', icon: 'bi-exclamation-octagon-fill' },
  // An assertion must never quietly become an assumption: when the rules could not be evaluated,
  // we say so instead of showing green.
  unverified: { color: '#cc8800', bg: '#fff8e6', icon: 'bi-question-octagon-fill' }
};

const VERDICT_STYLE = {
  action: { key: 'action', color: '#cc0000' },
  unverified: { key: 'unverified', color: '#cc8800' },
  ready: { key: 'ready', color: '#0a8f3c' },
  na: { key: 'na', color: '#adb5bd' }
};

// Soft badge rather than a solid block — a column of saturated chips reads as a wall of alarm.
// `accent` is the solid status colour, reused for the mobile card's edge stripe. Amber text is
// darkened from the chip's #cc8800 so it clears AA on its own tint.
const SOFT_BADGE = {
  action: { color: '#cc0000', bg: '#fdecec', accent: '#cc0000' },
  unverified: { color: '#9a6400', bg: '#fff4de', accent: '#cc8800' },
  ready: { color: '#0a8f3c', bg: '#e9f7ef', accent: '#0a8f3c' },
  na: { color: '#6c757d', bg: '#f1f1f4', accent: '#adb5bd' }
};

const badgeFor = (verdict) => SOFT_BADGE[(VERDICT_STYLE[verdict] || VERDICT_STYLE.unverified).key];

const StatusBadge = ({ verdict }) => {
  const { t } = useLanguage();
  const s = VERDICT_STYLE[verdict] || VERDICT_STYLE.unverified;
  const soft = SOFT_BADGE[s.key];
  return (
    <span className="arr-badge" style={{ color: soft.color, backgroundColor: soft.bg }}>
      {t(`departureReadiness.status.${s.key}`)}
    </span>
  );
};

// Per-column status. 'na' is an explicit, muted entry with its own icon — visually distinct from a
// green tick, never a quiet pass.
const COLUMN_STYLE = {
  ok: { key: 'ready', color: '#0a8f3c', icon: 'bi-check-circle-fill' },
  ready: { key: 'ready', color: '#0a8f3c', icon: 'bi-check-circle-fill' },
  action: { key: 'action', color: '#cc0000', icon: 'bi-exclamation-octagon-fill' },
  unverified: { key: 'unverified', color: '#cc8800', icon: 'bi-question-circle-fill' },
  pending: { key: 'pending', color: '#6c757d', icon: 'bi-hourglass-split' },
  na: { key: 'na', color: '#adb5bd', icon: 'bi-dash-circle' }
};

// Severity order — the operator reads their risk first, their day second.
const VERDICT_RANK = { action: 0, unverified: 1, ready: 2, na: 3 };

// These five PARTITION the rows — every departure lands in exactly one, so the chips always sum to
// the row count. `unverified` is included precisely because it is rare: a set of counters that
// quietly fails to add up on the one odd day teaches ops the numbers are decorative.
const CHIPS = [
  { key: 'action', label: 'action', color: '#cc0000' },
  { key: 'unverified', label: 'unverified', color: '#cc8800' },
  { key: 'pending', label: 'pending', color: '#6c757d' },
  { key: 'ready', label: 'ready', color: '#0a8f3c' },
  { key: 'na', label: 'na', color: '#adb5bd' }
];

const CardShell = ({ children, language }) => (
  <div className="card dep-readiness" dir={language === 'ar' ? 'rtl' : 'ltr'}>
    <div className="card-body">{children}</div>
  </div>
);

const Header = () => {
  const { t } = useLanguage();
  return (
    <div className="mb-1">
      <h5 className="card-title mb-0" style={{ color: '#471396' }}>
        {/* Deliberately paired with the arrival card's bi-door-open-fill. Non-directional on
            purpose: Bootstrap Icons are glyphs and are NOT mirrored by the RTL build, so an arrow
            variant would point the wrong way on this Arabic-first surface. */}
        <i className="bi bi-door-closed-fill me-2"></i>
        {t('departureReadiness.heading')}
      </h5>
      <small className="text-muted">{t('departureReadiness.subtitle')}</small>
    </div>
  );
};

/**
 * Collapse/expand — twin of the arrival card's. Open by default; the counters stay visible when
 * folded, so a collapsed card still answers "is anything wrong here?" without being reopened.
 * Chevron is UP/DOWN, never left/right (Bootstrap Icons are not mirrored by the RTL build).
 */
const CollapseToggle = ({ expanded, onToggle, label }) => (
  <button
    type="button"
    className="btn btn-sm btn-link p-0 text-decoration-none"
    onClick={onToggle}
    aria-expanded={expanded}
    aria-label={label}
    title={label}
    style={{ color: '#471396', fontSize: '18px', lineHeight: 1 }}
  >
    <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
  </button>
);

// The reminder column. The detail arrives from the server already worded (the engine owns it), so
// the card never rewords a verdict it did not make.
const StatusCell = ({ status, detail = null }) => {
  // Resolves its own translations: passing `t` through every call site makes a forgotten prop a
  // runtime crash.
  const { t } = useLanguage();
  const s = COLUMN_STYLE[status] || COLUMN_STYLE.na;
  return (
    <>
      <span style={{ color: s.color, fontWeight: 500, whiteSpace: 'nowrap' }}>
        <i className={`bi ${s.icon} me-1`}></i>{t(`departureReadiness.status.${s.key}`)}
      </span>
      {/* A green column's note stays MUTED — printed in the status colour it reads as a second
          green assertion; the tick already said everything. A red/amber note keeps the column's
          colour, because that is the sentence the operator is here to read. */}
      {detail && (
        s.key === 'ready'
          ? <small className="text-muted d-block">{detail}</small>
          : <small className="d-block" style={{ color: s.color }}>{detail}</small>
      )}
    </>
  );
};

/**
 * Copy the exact reminder text to the clipboard (§3.9 — the one genuine addition over the arrival
 * card). Ops confirm a manual send today but compose the message themselves, which drifts from the
 * automated wording guest by guest. The text is rendered server-side from the same template and the
 * same stored departure time the automation would have used.
 *
 * The text ships WITH the row rather than being fetched on click: `navigator.clipboard.writeText`
 * must run inside the user gesture, and an awaited fetch first loses that gesture in WebKit.
 */
const CopyReminderButton = ({ text }) => {
  const { t } = useLanguage();
  const [state, setState] = useState('idle'); // idle | copied | failed

  if (!text) return null;

  const copy = (e) => {
    e.stopPropagation(); // the row navigates on click
    const done = (next) => {
      setState(next);
      // Revert so the button is reusable — a permanently "copied" control reads as disabled.
      setTimeout(() => setState('idle'), 2000);
    };
    try {
      navigator.clipboard.writeText(text).then(() => done('copied'), () => done('failed'));
    } catch (_) {
      done('failed');
    }
  };

  const icon = state === 'copied' ? 'bi-clipboard-check' : state === 'failed' ? 'bi-clipboard-x' : 'bi-clipboard';
  const label = state === 'copied'
    ? t('departureReadiness.copy.copied')
    : state === 'failed'
      ? t('departureReadiness.copy.failed')
      : t('departureReadiness.copy.action');

  return (
    <button
      type="button"
      className="btn btn-sm btn-link p-0"
      onClick={copy}
      style={{
        fontSize: '12px',
        marginInlineStart: '8px',
        color: state === 'failed' ? '#cc0000' : '#471396',
        textDecoration: 'none',
        fontWeight: 600
      }}
    >
      <i className={`bi ${icon}`} style={{ marginInlineEnd: '4px' }} />
      {label}
    </button>
  );
};

/**
 * The one action an operator can take directly from this card: a booking with no OTA thread cannot
 * be served by the automation, so someone has to send the reminder personally and say so here.
 *
 * Deliberately quiet — an outline button sized to the row, not a primary CTA. It sits inside a
 * clickable row, so it stops propagation; without that, ticking would also navigate away and the
 * operator would never see the row clear.
 */
const ManualSendAction = ({ bookingId, previewText }) => {
  const { t } = useLanguage();
  const { mutate, isPending, isError, error } = useMarkCheckoutMessageSentManually();

  // Translate the CODE, never the server's message text (I18N_STRATEGY). The one refusal worth its
  // own words is "this booking is automated after all" — it means the row offering this button was
  // stale, so telling the operator to retry would be wrong advice.
  const code = error?.response?.data?.code;
  const errorKey = code === DEPARTURE_ERROR_CODES.MANUAL_SEND_NOT_APPLICABLE
    ? 'departureReadiness.manualSend.notApplicable'
    : 'departureReadiness.manualSend.failed';

  return (
    <div style={{ marginBlockStart: '4px' }}>
      {/* touch-btn-sm, not a bare btn-sm: this sits inside a row that navigates on click, so an
          undersized target turns a mistap into "you are now on a different page". */}
      <button
        type="button"
        className="btn btn-sm btn-outline-primary touch-btn-sm"
        disabled={isPending}
        onClick={(e) => {
          e.stopPropagation();
          mutate(bookingId);
        }}
        style={{
          // Bootstrap 5.3 buttons are driven by CSS variables, so setting these brands every state.
          // Overriding plain `color`/`borderColor` instead leaves :hover filling with Bootstrap's
          // default blue — brand purple text on a blue fill, off-brand and low contrast.
          '--bs-btn-color': '#471396',
          '--bs-btn-border-color': '#471396',
          '--bs-btn-hover-bg': '#471396',
          '--bs-btn-hover-border-color': '#471396',
          '--bs-btn-hover-color': '#fff',
          '--bs-btn-active-bg': '#350f70',
          '--bs-btn-active-border-color': '#350f70',
          fontWeight: 600
        }}
      >
        {isPending ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        ) : (
          <i className="bi bi-check2" style={{ marginInlineEnd: '4px' }} />
        )}
        {t('departureReadiness.manualSend.action')}
      </button>
      <CopyReminderButton text={previewText} />
      {isError && (
        <small className="d-block" style={{ color: '#cc0000' }}>
          {t(errorKey)}
        </small>
      )}
    </div>
  );
};

// The reminder column body — shared by the desktop table and the mobile block so the two can never
// drift apart in what they offer.
const ReminderCell = ({ row }) => {
  const { language } = useLanguage();
  const r = row.reminder || {};
  return (
    <>
      <StatusCell
        status={r.status}
        detail={isManualSendAttributed(r) ? null : r.detail}
      />
      {/* When the automation sent it, say WHEN — the manual row beside it carries «بواسطة X · time»,
          so an automated send with no timestamp read as the less-accounted-for of the two. */}
      {r.sentAt && (
        <small className="text-muted d-block">{formatShortDateTime(r.sentAt, language)}</small>
      )}
      <ManualSendAttribution reminder={r} bookingId={row.bookingId} />
      {/* The tick stays available once the moment has PASSED too — ops may have sent the reminder
          and not logged it, and an unrecordable send would leave the row permanently red for the
          wrong reason. The copy button does not: `previewText` is only ever populated while the
          send is genuinely due, so the card can never help compose a late reminder. */}
      {(r.code === DEPARTURE_REMINDER_CODES.MANUAL_SEND_REQUIRED
        || r.code === DEPARTURE_REMINDER_CODES.MANUAL_SEND_MISSED) && (
        <ManualSendAction bookingId={row.bookingId} previewText={r.previewText} />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
 * CHECKOUT RECORD COLUMN (tasks/manual-guest-checkout-event.md)
 *
 * The second signal a departure has, and the one this card's header reserved a slot for. It answers
 * a different question from the reminder column beside it: not "was the guest told when to leave?"
 * but "did they actually leave, and when?".
 *
 * 🔴 IT RECORDS, IT DOES NOT DISPATCH (D2). Pressing this notifies nobody and sends no cleaner.
 * 🔴 IT DOES NOT GRADE (D2/§9). Unrecorded reads grey, and never moves the row's `overall` verdict.
 * ───────────────────────────────────────────────────────────────────────────── */

/**
 * The checkout column's status line — deliberately NOT `StatusCell` (D19).
 *
 * `StatusCell` renders the shared `status.*` vocabulary in the shared palette, which would print a
 * recorded checkout as the same green «جاهز» tick the reminder column uses for a verified send. That
 * is exactly the equivalence this feature must never imply: the reminder column can PROVE what it
 * claims; this one carries a human's word about something no device watched.
 *
 * So: its own two words, its own tone (brand slate, matching the booking page's badge — never the
 * lock's green), and ledger iconography rather than a verdict tick. A device source, when it ships,
 * earns the stronger treatment here; a button does not.
 */
// 🔴 A GUEST'S PRESS AND AN OPS CONFIRMATION MAY NEVER READ THE SAME (D19). Both used to render
// «سُجّل», so a button pressed by somebody walking out of the door was indistinguishable from an
// operator who had phoned the guest and asked. That is the exact equivalence this column exists to
// avoid — and the whole reason `source` is carried all the way to the client.
//
// Same ledger iconography and the same restrained tone: this is still a record, not a verdict. The
// difference is only in WHOSE word it is, which is the one thing an operator needs to read at a
// glance before acting on it.
const CHECKOUT_STYLE = {
  recorded: { key: 'recorded', color: '#3f3591', icon: 'bi-journal-check' },
  recordedByGuest: { key: 'recordedByGuest', color: '#3f3591', icon: 'bi-person-check' },
  notRecorded: { key: 'notRecorded', color: '#6c757d', icon: 'bi-journal' }
};

const CheckoutStatusLine = ({ recorded, source, detail }) => {
  const { t } = useLanguage();
  const s = !recorded
    ? CHECKOUT_STYLE.notRecorded
    : (source === 'guest' ? CHECKOUT_STYLE.recordedByGuest : CHECKOUT_STYLE.recorded);
  return (
    <>
      {/* marginInlineEnd, not `me-1`: the RTL Bootstrap build is loaded app-wide, so its logical
          classes resolve to the wrong side inside an English (`dir="ltr"`) render — invisible in
          Arabic, silently wrong in English (I18N_STRATEGY). The sibling StatusCell still uses the
          class; it is pre-existing debt, converged opportunistically, not swept. */}
      <span style={{ color: s.color, fontWeight: 500, whiteSpace: 'nowrap' }}>
        <i className={`bi ${s.icon}`} style={{ marginInlineEnd: '4px' }}></i>
        {t(`departureReadiness.checkout.state.${s.key}`)}
      </span>
      {detail && <small className="text-muted d-block">{detail}</small>}
    </>
  );
};

/**
 * Who confirmed the checkout, when the guest left, and the way back out.
 *
 * The hour shown is `observedAt` — when the GUEST LEFT — not when the button was pressed. Ops
 * routinely confirm at 14:00 that a guest left at 09:15, which is the whole reason the two are
 * stored separately (D14). `recordedAt` is deliberately NOT rendered: it is audit data, it lives on
 * the booking's change log, and putting two timestamps in one table cell invites the operator to
 * read the wrong one.
 */
const CheckoutAttribution = ({ checkout, bookingId }) => {
  const { t, language } = useLanguage();
  const who = checkout.recordedByName ? ` · ${t('departureReadiness.checkout.by')} ${checkout.recordedByName}` : '';

  return (
    <small className="text-muted d-block">
      {t('departureReadiness.checkout.left')} {formatShortDateTime(checkout.observedAt, language)}{who}
      {/* `checkout.source` is the record the backend resolved as governing — an ops confirmation or
          the guest's own plaque report. It must reach the undo, or a guest report is silently
          withdrawn as if it were ops'. */}
      <UndoCheckout bookingId={bookingId} source={checkout.source} />
    </small>
  );
};

/**
 * Un-record. Kept visually quiet — a plain link, not a button — because this is a correction, not
 * an action anyone should be invited to take. Without it, a press on the wrong row would leave a
 * booking carrying a departure that never happened.
 */
const UndoCheckout = ({ bookingId, source }) => {
  const { t } = useLanguage();
  const { mutate, isPending } = useUndoGuestCheckout();

  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0"
      disabled={isPending}
      onClick={(e) => {
        e.stopPropagation();
        // The SOURCE of the record on screen, not a default. Withdrawing is per-record now, and
        // omitting this against a guest's plaque report silently withdraws nothing.
        mutate({ bookingId, source });
      }}
      style={{ fontSize: '12px', marginInlineStart: '8px', verticalAlign: 'baseline' }}
    >
      {t('departureReadiness.checkout.undo')}
    </button>
  );
};

/**
 * Confirm a checkout: a button that opens a small time entry, defaulting to now and editable
 * BACKWARDS only.
 *
 * Two steps rather than one-click-equals-now, deliberately. A single click would silently assert
 * that the guest left at the moment ops got round to pressing it — which on a morning of ten
 * departures confirmed in one sitting is false for nine of them, and would poison the very
 * measurement that would justify buying keypads (D10/D14).
 *
 * The value goes through `riyadhInputValueToIso`: a datetime-local field hands back a naive
 * wall-clock string that the browser reads in ITS OWN zone, so an operator outside Saudi would
 * otherwise record an hour nobody typed.
 */
const CheckoutAction = ({ bookingId, secondary = false }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const { mutate, isPending, isError, error } = useRecordGuestCheckout();

  const stop = (e) => e.stopPropagation(); // the row navigates on click

  const openEntry = (e) => {
    stop(e);
    // Seeded fresh on each open, never at mount: a card left sitting for an hour would otherwise
    // prefill a stale "now".
    setValue(toRiyadhInputValue(new Date()));
    setOpen(true);
  };

  const save = (e) => {
    stop(e);
    const observedAt = riyadhInputValueToIso(value);
    if (!observedAt) return; // the browser blocks an empty/invalid datetime-local; belt and braces
    mutate({ bookingId, observedAt }, { onSuccess: () => setOpen(false) });
  };

  // Translate the CODE, never the server's message text (I18N_STRATEGY).
  const code = error?.response?.data?.code;
  const errorKey = code === DEPARTURE_ERROR_CODES.CHECKOUT_TIME_INVALID
    ? 'departureReadiness.checkout.timeInvalid'
    : code === DEPARTURE_ERROR_CODES.CHECKOUT_NOT_APPLICABLE
      ? 'departureReadiness.checkout.notApplicable'
      : 'departureReadiness.checkout.failed';

  if (!open) {
    return (
      <div style={{ marginBlockStart: '4px' }}>
        {/* touch-btn-sm, not a bare btn-sm: this sits inside a row that navigates on click, so an
            undersized target turns a mistap into "you are now on a different page".

            🔴 `secondary` IS THE DIFFERENCE BETWEEN A TASK AND AN OPTION. On a row where the guest
            has already reported, nothing is required of ops — the record exists and the crew have
            been told. Offering the identical button there made three rows look like three
            outstanding jobs, which is how a daily list stops being read. Quiet styling and «تأكيد
            الفريق» say "override this if you know better"; the solid «تأكيد الخروج» stays for rows
            where nobody has said anything at all. Same treatment the booking page already gives
            this pair — the table simply never got it. */}
        <button
          type="button"
          className={`btn btn-sm touch-btn-sm ${secondary ? 'btn-link p-0' : 'btn-outline-primary'}`}
          onClick={openEntry}
          // ⚠️ UNDERLINED, and that is not a detail. The first attempt at the quiet variant stripped
          // the underline as well as the fill, and it stopped looking like a control at all — a grey
          // label sitting under the record. Its sibling «تراجع» in the same cell is an underlined
          // link, so this matches it: demoted from the solid button, still obviously pressable.
          style={secondary ? { fontSize: '12px' } : CHECKOUT_BTN_STYLE}
        >
          {!secondary && <i className="bi bi-box-arrow-right" style={{ marginInlineEnd: '4px' }} />}
          {t(secondary ? 'departureReadiness.checkout.opsOverride' : 'departureReadiness.checkout.action')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginBlockStart: '6px' }} onClick={stop}>
      {/* The input is NESTED in its label rather than paired by id/htmlFor: several rows can have
          their entry open at once, so any fixed id would be duplicated across the table. Nesting
          associates them without an id at all. */}
      <label className="form-label mb-1 d-block" style={{ fontSize: '12px', fontWeight: 600 }}>
        {t('departureReadiness.checkout.timeLabel')}
        <input
          type="datetime-local"
          className="form-control form-control-sm"
          value={value}
          // A departure cannot be in the future — enforced here for immediate feedback AND
          // server-side, because a max attribute is a hint, not a guarantee.
          max={toRiyadhInputValue(new Date())}
          onChange={(e) => setValue(e.target.value)}
          onClick={stop}
          style={{ maxWidth: '210px', fontSize: '13px', marginBlockStart: '4px' }}
        />
      </label>
      <small className="text-muted d-block" style={{ fontSize: '11px', marginBlockStart: '2px' }}>
        {t('departureReadiness.checkout.timeHint')}
      </small>
      <div className="d-flex gap-2" style={{ marginBlockStart: '6px' }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary touch-btn-sm"
          disabled={isPending || !value}
          onClick={save}
          style={CHECKOUT_BTN_STYLE}
        >
          {isPending
            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            : <i className="bi bi-check2" style={{ marginInlineEnd: '4px' }} />}
          {t('departureReadiness.checkout.save')}
        </button>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={(e) => { stop(e); setOpen(false); }}
          style={{ fontSize: '12px' }}
        >
          {t('departureReadiness.checkout.cancel')}
        </button>
      </div>
      {isError && (
        <small className="d-block" style={{ color: '#cc0000' }}>{t(errorKey)}</small>
      )}
    </div>
  );
};

// Bootstrap 5.3 buttons are driven by CSS variables, so setting these brands every state.
// Overriding plain `color`/`borderColor` instead leaves :hover filling with Bootstrap's default
// blue — brand purple text on a blue fill, off-brand and low contrast. Same set as ManualSendAction.
const CHECKOUT_BTN_STYLE = {
  '--bs-btn-color': '#471396',
  '--bs-btn-border-color': '#471396',
  '--bs-btn-hover-bg': '#471396',
  '--bs-btn-hover-border-color': '#471396',
  '--bs-btn-hover-color': '#fff',
  '--bs-btn-active-bg': '#350f70',
  '--bs-btn-active-border-color': '#350f70',
  fontWeight: 600
};

// The checkout column body — shared by the desktop table and the mobile block, so the two can never
// drift apart in what they offer. Twin of ReminderCell.
const CheckoutCell = ({ row }) => {
  const c = row.checkout || {};
  const recorded = c.code === DEPARTURE_CHECKOUT_CODES.RECORDED;
  return (
    <>
      {/* When recorded, the attribution line below IS the statement, so the status line carries no
          detail — same suppression rule the reminder column applies to its own attribution. */}
      <CheckoutStatusLine recorded={recorded} source={c.source} detail={recorded ? null : c.detail} />
      {recorded
        ? <CheckoutAttribution checkout={c} bookingId={row.bookingId} />
        : <CheckoutAction bookingId={row.bookingId} />}
      {/* 🔴 OPS CAN STILL CONFIRM ON TOP OF A GUEST'S REPORT (D43/D47).
          A guest press and an ops confirmation are meant to COEXIST, with ops taking precedence —
          that is the whole point of the precedence rule. But this cell only ever offered "record"
          when nothing was recorded, so the moment a guest pressed the plaque the only action left
          was «تراجع»: to add their own confirmation an operator had to first DESTROY the guest's
          report. Found in a dev walkthrough, not by reading the code.
          Offered only against a GUEST record — confirming on top of an ops record would be
          confirming your own confirmation.
          `secondary`: an OPTION, not a task. See the button itself for why that matters here. */}
      {recorded && c.source === 'guest' && <CheckoutAction bookingId={row.bookingId} secondary />}
    </>
  );
};

const DepartureReadinessCard = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { report, isLoading, isError, refetch } = useDepartureReadiness();
  const [expanded, setExpanded] = useState(true);
  // The «محجوز / الضيف الآن في» strip, exactly as the arrivals card carries it. Every figure on this
  // card now resolves from the unit the guest is actually in, so the label is already right — this
  // says WHY it differs from the booking the operator may be holding in their head.
  //
  // ⚠️ Above the loading/error returns, not beside the rows: hooks must run in the same order on
  // every render, and the early exits below would otherwise skip it.
  // ⚠️ DESTRUCTURE. The hook returns `{ relocations, failed, loading }`, so assigning the whole
  // object makes every `relocations[bookingId]` undefined and the strip renders nowhere — while the
  // unit label beside it has already switched to the receiving unit. The operator then reads a row
  // naming an apartment that is not the one on the booking, with nothing explaining why.
  const departureRows = report?.departures || [];
  const { relocations, failed: relocationsFailed } = useRelocations(
    departureRows.map((d) => d.bookingId)
  );

  if (isLoading) {
    return (
      <CardShell language={language}>
        <Header />
        <div className="text-center p-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">{t('departureReadiness.loading')}</span>
          </div>
        </div>
      </CardShell>
    );
  }

  if (isError) {
    return (
      <CardShell language={language}>
        <Header />
        <div className="alert alert-danger mb-0 mt-2 d-flex justify-content-between align-items-center">
          <span>{t('departureReadiness.error')}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={() => refetch()}>{t('departureReadiness.retry')}</button>
        </div>
      </CardShell>
    );
  }

  const { summary, departures, headline } = report;
  const head = HEADLINE_STYLE[headline] || HEADLINE_STYLE.unverified;
  const headKey = HEADLINE_STYLE[headline] ? headline : 'unverified';

  const rows = [...(departures || [])].sort(
    (a, b) => (VERDICT_RANK[a.overall] ?? 9) - (VERDICT_RANK[b.overall] ?? 9)
  );

  return (
    <CardShell language={language}>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <CollapseToggle
            expanded={expanded}
            onToggle={() => setExpanded((v) => !v)}
            label={t('departureReadiness.heading')}
          />
          <Header />
        </div>
        <div className="d-flex gap-3 flex-wrap">
          {CHIPS.map((c) => (
            <div key={c.key} className="text-center" style={{ minWidth: '64px' }}>
              <div style={{ fontWeight: 700, fontSize: '22px', color: summary?.[c.key] > 0 ? c.color : '#adb5bd' }}>
                {summary?.[c.key] ?? 0}
              </div>
              <small className="text-muted">{t(`departureReadiness.chip.${c.label}`)}</small>
            </div>
          ))}
        </div>
      </div>

      {/* The day's answer in one line — so nobody has to aggregate a column of rows in their head.

          Suppressed when there are NO departures: «كل تسجيل الخروج اليوم في مساره — التذكير أُرسل
          أو في طريقه لكل ضيف يغادر اليوم» is an assertion about guests who do not exist, and it
          would sit directly above «لا يوجد تسجيل خروج اليوم». A card that verified nothing must
          say nothing. (Same fix applied to the arrival card, where it was live on prod.) */}
      {/* Silence is the one wrong answer here: without it a failed lookup reads as "nobody moved",
          on a card whose unit labels have already switched to wherever the guest actually is.
          Same shape, wording and placement as the arrivals card. */}
      {expanded && relocationsFailed && (
        <div className="alert alert-warning py-2 px-3 mb-3" dir="rtl" role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true"></i> {RELOCATION_UNKNOWN_AR}
        </div>
      )}

      {expanded && rows.length > 0 && (
        <div
          className="d-flex align-items-start gap-3 mb-3 p-3"
          style={{
            backgroundColor: head.bg,
            borderRight: `4px solid ${head.color}`,
            borderRadius: '6px'
          }}
        >
          <i className={`bi ${head.icon}`} style={{ color: head.color, fontSize: '20px', lineHeight: 1.4 }}></i>
          <div>
            <div style={{ color: head.color, fontWeight: 700, fontSize: '16px' }}>{t(`departureReadiness.headline.${headKey}`)}</div>
            <small className="text-muted">{t(`departureReadiness.headline.${headKey}Sub`)}</small>
          </div>
        </div>
      )}

      {/* Everything below folds away; the counters above stay. */}
      {expanded && (
        <>


      {/* Stated ONCE at the top rather than leaving ops to infer a system-wide switch from a column
          of red rows. Raised only when it actually bites — the switch is off AND at least one of
          today's departures sits at a property that was expecting the automation to act. */}
      {report.automationOff && (
        <div className="alert alert-danger py-2 mb-3">
          <i className="bi bi-exclamation-octagon me-2"></i>
          {t('departureReadiness.automationOff')}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-muted d-flex align-items-center gap-2">
          <i className="bi bi-calendar-x"></i>
          <span>{t('departureReadiness.empty')}</span>
        </div>
      ) : (
        <>
          {/* Mobile: one stacked block per guest — RTL columns on a phone would mean horizontal
              scrolling to reach the very statuses the card exists to show. Same d-none d-md-block /
              d-md-none split the arrival card and the ops performance dashboard use. */}
          <div className="d-md-none">
            {rows.map((r) => {
              const soft = badgeFor(r.overall);
              return (
                <div
                  key={r.bookingId}
                  className="arr-m-row"
                  onClick={() => navigate(`/bookings/${r.bookingId}`)}
                  style={{
                    cursor: 'pointer',
                    opacity: r.overall === 'ready' ? 0.85 : 1,
                    // the status colour as an edge stripe, so a guest block reads its verdict
                    // before any label is read
                    borderInlineStartColor: soft.accent
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-semibold" style={{ color: '#090040' }}>{r.guestName || '—'}</div>
                      <small className="text-muted d-block">{r.propertyLabel || '—'}</small>
                      <RelocationBanner relocation={relocations[r.bookingId]} compact />
                      {/* Desktop-only until now — a phone row named a guest with no
                          booking reference to act on. */}
                      <BookingRefLine confirmationCode={r.confirmationCode} platform={r.platform} />
                    </div>
                    <div className="text-start flex-shrink-0">
                      <StatusBadge verdict={r.overall} />
                      <small className="text-muted d-block mt-1">
                        {r.departureAt ? formatShortDateTime(r.departureAt, language) : '—'}
                      </small>
                    </div>
                  </div>
                  {/* Two .arr-m-sub blocks — the second is the slot this card reserved for "did
                      they actually leave?", now filled by the checkout record. A device source
                      writes into the SAME block later; it does not earn a third. */}
                  <div className="arr-m-sub">
                    <span className="arr-label">{t('departureReadiness.column.reminder')}</span>
                    <div><ReminderCell row={r} /></div>
                  </div>
                  <div className="arr-m-sub">
                    <span className="arr-label">{t('departureReadiness.column.checkout')}</span>
                    <div><CheckoutCell row={r} /></div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover align-middle mb-0 arr-table">
              <thead>
                <tr>
                  <th>{t('departureReadiness.column.guest')}</th>
                  <th>{t('departureReadiness.column.property')}</th>
                  <th>{t('departureReadiness.column.time')}</th>
                  <th>{t('departureReadiness.column.reminder')}</th>
                  <th>{t('departureReadiness.column.checkout')}</th>
                  <th>{t('departureReadiness.column.status')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.bookingId}
                    onClick={() => navigate(`/bookings/${r.bookingId}`)}
                    style={{ cursor: 'pointer', opacity: r.overall === 'ready' ? 0.75 : 1 }}
                  >
                    <td style={{ fontWeight: 500 }}>
                      {r.guestName || '—'}
                      <BookingRefLine confirmationCode={r.confirmationCode} platform={r.platform} />
                    </td>
                    <td>
                      {r.propertyLabel || '—'}
                      <RelocationBanner relocation={relocations[r.bookingId]} compact />
                    </td>
                    <td><small>{r.departureAt ? formatShortDateTime(r.departureAt, language) : '—'}</small></td>
                    <td><ReminderCell row={r} /></td>
                    <td><CheckoutCell row={r} /></td>
                    <td><StatusBadge verdict={r.overall} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* States the window rule plainly. Without it, a red row nobody can fix reads as an
          unattended task and gets chased; with it, ops know it is a record, not a to-do. */}
      <small className="text-muted d-block mt-3">
        <i className="bi bi-info-circle" style={{ marginInlineEnd: '6px' }} />
        {t('departureReadiness.windowNote')}
      </small>
        </>
      )}
    </CardShell>
  );
};

export default DepartureReadinessCard;
